import { FYMAPIError } from 'fym-common/src/generics/errors/FYMAPIError';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import { musicServicesByMusicAPIID } from 'fym-shared/src/music-services/allMusicServices';
import { CatalogItem, CatalogMergeOption } from '../../../../redux/api/fymAPI.types';
import { ErrorAPIResult } from '../../../../redux/api/utils/isSuccessfulResult';

/** The picker sections (design catalog-merge a2). */
export interface MergeSections {
  likely: CatalogMergeOption[];
  others: CatalogMergeOption[];
}

/** The candidates in two sections, filtered by the search text (title, no case). */
export function mergeSections(candidates: CatalogMergeOption[], search: string): MergeSections {
  const needle = search.trim().toLowerCase();
  const shown = needle === '' ? candidates : candidates.filter((row) => row.title.toLowerCase().includes(needle));
  return {
    likely: shown.filter((row) => row.likelyDuplicate),
    others: shown.filter((row) => !row.likelyDuplicate),
  };
}

/** The row that the picker selects first: the first likely duplicate, else none. */
export function preselectedMergeUuid(candidates: CatalogMergeOption[]): string | undefined {
  return candidates.find((row) => row.likelyDuplicate)?.uuid;
}

/** The two rows of a merge: the row that stays and the row that leaves the catalog. */
export interface MergePair {
  kept: CatalogItem;
  leaving: CatalogItem;
}

/**
 * The pair of a merge of `source` with the picked `target`. `keepUuid` of the candidate names the
 * row that stays (the row the artist made, else the older row). Undefined when a row is not in the
 * catalog list.
 */
export function mergePair(
  source: CatalogItem,
  target: Pick<CatalogMergeOption, 'uuid' | 'keepUuid'>,
  items: CatalogItem[]
): MergePair | undefined {
  const other = items.find((row) => row.uuid === target.uuid);
  if (other === undefined) {
    return undefined;
  }
  return target.keepUuid === source.uuid ? { kept: source, leaving: other } : { kept: other, leaving: source };
}

/** One "gets" line of the confirm step (design catalog-merge a3). */
export type MergeGetLine =
  | { kind: 'link'; service: string }
  | { kind: 'date'; date: string }
  | { kind: 'cover' }
  | { kind: 'made' };

function serviceName(integrationType: string): string {
  return musicServicesByMusicAPIID[integrationType]?.name ?? integrationType;
}

function hasArtwork(item: CatalogItem): boolean {
  return item.artworkFile !== null || item.artworkUrl !== null;
}

/**
 * What the kept row gets: a link line per service it has not, the date line when it is unreleased
 * and the other row is released, the cover line when it has no artwork, and the made things line.
 */
export function mergeGetLines({ kept, leaving }: MergePair): MergeGetLine[] {
  const keptLinks = kept.serviceLinks ?? {};
  const links: MergeGetLine[] = Object.keys(leaving.serviceLinks ?? {})
    .filter((service) => keptLinks[service] === undefined)
    .map((service) => ({ kind: 'link', service: serviceName(service) }));
  const date: MergeGetLine[] =
    kept.kind === CatalogItemKind.unreleased &&
    leaving.kind === CatalogItemKind.claimed &&
    leaving.releaseDate !== null &&
    leaving.releaseDate !== kept.releaseDate
      ? [{ kind: 'date', date: leaving.releaseDate }]
      : [];
  const cover: MergeGetLine[] = !hasArtwork(kept) && hasArtwork(leaving) ? [{ kind: 'cover' }] : [];
  return [...links, ...date, ...cover, { kind: 'made' }];
}

/** How the confirm step shows a failed merge (design catalog-merge a6). */
export type MergeErrorKind = 'changed' | 'failed';

/** 409 and 404: a row changed or went, so the catalog must load again. Every other error: try again. */
export function mergeErrorKind(error: ErrorAPIResult['error'] | undefined): MergeErrorKind {
  if (error instanceof FYMAPIError && (error.statusCode === 409 || error.statusCode === 404)) {
    return 'changed';
  }
  return 'failed';
}

/** "Merge with another song…" shows with two songs or more, and never for the demo artist. */
export function canOfferMerge(songCount: number, isDemo: boolean): boolean {
  return !isDemo && songCount >= 2;
}
